import React, { useMemo, useState } from "react";
import { MessageSquare, Mail, Phone, Code2 } from "lucide-react";

/**
 * Editor for the `MarksMessage` column, which stores a per-channel
 * notification-template config as a JSON string.
 *
 * Verified shape (100% of rows in triangles + rvlh are valid JSON):
 *   {
 *     sms:      { smsmessage, presentMessage, absentMessage, sendTo,
 *                 smsmessagepostboxTemplateId },
 *     email:    { mailsubject, mailmessage, presentMessage, absentMessage,
 *                 sendTo, mailmessagepostboxTemplateId },
 *     whatsApp: { whatsappsubject, whatsappmessage, whatsapptemplatename,
 *                 presentMessage, absentMessage, sendTo,
 *                 whatsappmessagepostboxTemplateId }
 *   }
 *
 * Placeholders like ${testDate}, ${marks1}, ${percentage1} are substituted
 * downstream — treat message bodies as opaque strings. Unknown keys (top-level
 * or per-channel) are preserved on every write.
 */

type FieldKind = "text" | "textarea";
interface FieldDef {
  key: string;
  label: string;
  kind: FieldKind;
  hint?: string;
}

interface ChannelDef {
  key: "sms" | "email" | "whatsApp";
  label: string;
  icon: React.ReactNode;
  fields: FieldDef[];
}

const RECIPIENT_HINT = "Comma-separated, e.g. STUDENT,FATHER,MOTHER,GUARDIAN";

const CHANNELS: ChannelDef[] = [
  {
    key: "sms",
    label: "SMS",
    icon: <MessageSquare size={15} />,
    fields: [
      { key: "smsmessage", label: "SMS Message", kind: "textarea" },
      { key: "presentMessage", label: "Present Message", kind: "textarea" },
      { key: "absentMessage", label: "Absent Message", kind: "textarea" },
      { key: "sendTo", label: "Send To", kind: "text", hint: RECIPIENT_HINT },
      {
        key: "smsmessagepostboxTemplateId",
        label: "Postbox Template ID",
        kind: "text",
      },
    ],
  },
  {
    key: "email",
    label: "Email",
    icon: <Mail size={15} />,
    fields: [
      { key: "mailsubject", label: "Email Subject", kind: "text" },
      { key: "mailmessage", label: "Email Message", kind: "textarea" },
      { key: "presentMessage", label: "Present Message", kind: "textarea" },
      { key: "absentMessage", label: "Absent Message", kind: "textarea" },
      { key: "sendTo", label: "Send To", kind: "text", hint: RECIPIENT_HINT },
      {
        key: "mailmessagepostboxTemplateId",
        label: "Postbox Template ID",
        kind: "text",
      },
    ],
  },
  {
    key: "whatsApp",
    label: "WhatsApp",
    icon: <Phone size={15} />,
    fields: [
      { key: "whatsappsubject", label: "WhatsApp Subject", kind: "text" },
      { key: "whatsappmessage", label: "WhatsApp Message", kind: "textarea" },
      {
        key: "whatsapptemplatename",
        label: "WhatsApp Template Name",
        kind: "text",
      },
      { key: "presentMessage", label: "Present Message", kind: "textarea" },
      { key: "absentMessage", label: "Absent Message", kind: "textarea" },
      { key: "sendTo", label: "Send To", kind: "text", hint: RECIPIENT_HINT },
      {
        key: "whatsappmessagepostboxTemplateId",
        label: "Postbox Template ID",
        kind: "text",
      },
    ],
  },
];

interface Props {
  /** Raw MarksMessage column value (JSON string). */
  value: string | null;
  onChange: (nextRaw: string) => void;
}

const MarksMessageEditor: React.FC<Props> = ({ value, onChange }) => {
  const [showRaw, setShowRaw] = useState(false);

  const { parsed, parseError } = useMemo(() => {
    const raw = (value ?? "").trim();
    if (!raw) return { parsed: {} as Record<string, any>, parseError: false };
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        return { parsed: obj as Record<string, any>, parseError: false };
      }
      return { parsed: {} as Record<string, any>, parseError: true };
    } catch {
      return { parsed: {} as Record<string, any>, parseError: true };
    }
  }, [value]);

  const setField = (channel: string, field: string, val: string) => {
    const next = {
      ...parsed,
      [channel]: { ...(parsed[channel] || {}), [field]: val },
    };
    onChange(JSON.stringify(next, null, 2));
  };

  // Legacy / non-JSON value: fall back to a plain textarea so we never
  // silently discard existing content.
  if (parseError) {
    return (
      <div>
        <div className="tcs-alert tcs-alert-danger">
          This category's Marks Message is not valid JSON. Editing it as plain
          text — switch to structured once it is valid JSON.
        </div>
        <textarea
          className="tcs-textarea"
          rows={5}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="tcs-variant-head">
        <p className="tcs-hint" style={{ margin: 0 }}>
          Per-channel result notification templates. Placeholders like{" "}
          <code>{"${testDate}"}</code>, <code>{"${marks1}"}</code>,{" "}
          <code>{"${percentage1}"}</code> are filled in when the message is
          sent.
        </p>
        <button
          type="button"
          className="tcs-btn tcs-btn-secondary tcs-btn-sm"
          onClick={() => setShowRaw((s) => !s)}
        >
          <Code2 size={13} /> {showRaw ? "Hide" : "Show"} Raw JSON
        </button>
      </div>

      {CHANNELS.map((ch) => {
        const data = parsed[ch.key] || {};
        const configured = !!parsed[ch.key];
        return (
          <div key={ch.key} className="tcs-variant">
            <div className="tcs-variant-head">
              <span className="tcs-variant-key">
                {ch.icon} {ch.label}
              </span>
              <span
                className={`tcs-pill ${
                  configured ? "tcs-pill-success" : "tcs-pill-neutral"
                }`}
              >
                {configured ? "Configured" : "Not set"}
              </span>
            </div>
            {ch.fields.map((f) => (
              <div className="tcs-field" key={f.key}>
                <label className="tcs-label">{f.label}</label>
                {f.hint && <p className="tcs-hint">{f.hint}</p>}
                {f.kind === "textarea" ? (
                  <textarea
                    className="tcs-textarea"
                    rows={2}
                    value={data[f.key] ?? ""}
                    onChange={(e) => setField(ch.key, f.key, e.target.value)}
                  />
                ) : (
                  <input
                    className="tcs-input"
                    value={data[f.key] ?? ""}
                    onChange={(e) => setField(ch.key, f.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}

      {showRaw && (
        <div className="tcs-field">
          <label className="tcs-label">Marks Message (raw JSON)</label>
          <p className="tcs-hint">
            Edits here update the fields above. Any keys not shown above are
            preserved.
          </p>
          <textarea
            className="tcs-textarea tcs-code"
            rows={14}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default MarksMessageEditor;
